import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, Building2 } from 'lucide-react';
import { useTenants, useUsers } from '@/api/hooks';
import { useAppStore } from '@/stores/app-store';
import { NAV_ITEMS } from '@/lib/nav';
import { useIsSuperAdmin } from '@/lib/auth';
import { Badge } from '@/components/Badge';
import { cn } from '@/lib/utils';

/** Custom event name used by UI affordances (e.g. sidebar button) to open the palette. */
export const OPEN_PALETTE_EVENT = 'mc:command-palette';

interface PaletteItem {
  key: string;
  label: string;
  hint?: string;
  section: 'Pages' | 'Tenants' | 'Users';
  icon: React.ComponentType<{ className?: string }>;
  action: () => void;
}

const TENANT_CAP = 8;
const USER_CAP = 6;

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const token = useAppStore((s) => s.token);
  const isSuperAdmin = useIsSuperAdmin();
  const { data: tenants } = useTenants();
  // Users list is heavier (membership summaries) — only fetch once the palette opens
  const { data: users } = useUsers(open);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (useAppStore.getState().token) setOpen((o) => !o);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_PALETTE_EVENT, onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_PALETTE_EVENT, onOpen);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlight(0);
      // Focus after mount paint
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const items = useMemo<PaletteItem[]>(() => {
    const q = query.trim().toLowerCase();
    const pageItems = NAV_ITEMS.filter((i) => (!i.superAdminOnly || isSuperAdmin) && (!q || i.label.toLowerCase().includes(q))).map((i) => ({
      key: `page:${i.path}`,
      label: i.label,
      hint: 'Page',
      section: 'Pages' as const,
      icon: i.icon,
      action: () => navigate(i.path),
    }));
    const tenantItems = (tenants ?? [])
      .filter((t) => !q || t.name.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, TENANT_CAP)
      .map((t) => ({
        key: `tenant:${t.id}`,
        label: t.name,
        hint: t.typeOfTenantID,
        section: 'Tenants' as const,
        icon: Building2,
        action: () => navigate(`/tenants/${t.id}`),
      }));
    const userItems = (users ?? [])
      .filter((u) => {
        if (!q) return false;
        const name = [u.firstname, u.lastname].filter(Boolean).join(' ') || u.email;
        return name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
      })
      .slice(0, USER_CAP)
      .map((u) => ({
        key: `user:${u.id}`,
        label: [u.firstname, u.lastname].filter(Boolean).join(' ') || u.email,
        hint: u.email,
        section: 'Users' as const,
        icon: Users,
        action: () => navigate(`/users/${u.id}`),
      }));
    return [...pageItems, ...tenantItems, ...userItems];
  }, [query, tenants, users, navigate]);

  useEffect(() => setHighlight(0), [items.length]);

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-idx="${highlight}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  if (!open || !token) return null;

  const close = () => setOpen(false);
  const run = (item: PaletteItem) => {
    close();
    item.action();
  };

  let lastSection = '';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[14vh] px-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px] animate-fade-in" onClick={close} />
      <div className="relative w-full max-w-md rounded-xl border bg-popover shadow-pop animate-scale-in overflow-hidden">
        <div className="flex items-center gap-2.5 border-b border-border px-3.5">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Jump to a tenant or page…"
            className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlight((h) => Math.min(h + 1, items.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlight((h) => Math.max(h - 1, 0));
              } else if (e.key === 'Enter' && items[highlight]) {
                e.preventDefault();
                run(items[highlight]);
              } else if (e.key === 'Escape') {
                close();
              }
            }}
          />
          <kbd className="shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            esc
          </kbd>
        </div>

        <div ref={listRef} className="max-h-80 overflow-auto p-1.5">
          {items.length === 0 ? (
            <div className="px-3 py-8 text-center text-xs text-muted-foreground">
              No matches for “{query}”
            </div>
          ) : (
            items.map((item, idx) => {
              const sectionHeader =
                item.section !== lastSection ? ((lastSection = item.section), item.section) : null;
              const Icon = item.icon;
              return (
                <div key={item.key}>
                  {sectionHeader && (
                    <div className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {sectionHeader}
                    </div>
                  )}
                  <button
                    type="button"
                    data-idx={idx}
                    onMouseEnter={() => setHighlight(idx)}
                    onClick={() => run(item)}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors',
                      idx === highlight ? 'bg-accent text-accent-foreground' : 'text-foreground'
                    )}
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.hint && item.section === 'Tenants' ? (
                      <Badge variant="outline" className="text-[9px] shrink-0">
                        {item.hint}
                      </Badge>
                    ) : (
                      <span className="text-[10px] text-muted-foreground shrink-0">{item.hint}</span>
                    )}
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className="border-t border-border px-3.5 py-2 flex items-center gap-4 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <kbd className="rounded border border-border bg-muted px-1">↑↓</kbd> navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="rounded border border-border bg-muted px-1">↵</kbd> open
          </span>
          <span className="ml-auto">{(tenants ?? []).length} tenants</span>
        </div>
      </div>
    </div>
  );
}
